import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { getFlagInfo, getAddedFlags, getRemovedFlags } from './useYardTaskFlaggedEvent.helpers';
import useYardTaskEvent from './useYardTaskEvent';

import type { YardTaskTimelineEvent } from '../../composables/useYardTaskTimeline';
import type { UseYardTaskEventOptions } from './useYardTaskEvent';

/**
 * Composable for flagged event that extends base event composable
 * @param {YardTaskTimelineEvent} event
 * @param {UseYardTaskEventOptions} options
 * @returns {Object} Base event data plus flagged-specific data
 */
export default function useYardTaskFlaggedEvent(
  event: YardTaskTimelineEvent,
  options: UseYardTaskEventOptions
) {
  const { t } = useI18n();
  const baseEventData = useYardTaskEvent(event, options);

  const addedFlags = computed(() => {
    return getAddedFlags(event);
  });

  const removedFlags = computed(() => {
    return getRemovedFlags(event);
  });

  const flagInfo = computed(() => getFlagInfo(event));

  const message = computed<string[]>(() => {
    // TODO: (welington) Should we consider multiple flags being added/removed at the same time?
    const { type, action } = flagInfo.value;

    if (type === 'issue' && action === 'added') {
      return [t('global.timeline.reportedAn'), t('global.basicTerms.issue')];
    }

    if (type === 'issue' && action === 'removed') {
      return [t('global.timeline.clearedThe'), t('global.basicTerms.issue')];
    }

    if (type === 'urgent' && action === 'added') {
      return [t('global.timeline.flaggedTheTaskAs'), t('global.basicTerms.urgent')];
    }

    if (type === 'urgent' && action === 'removed') {
      return [t('global.timeline.unflaggedTheTaskAs'), t('global.basicTerms.urgent')];
    }

    if (type && action === 'added') {
      return [t('global.timeline.flaggedTheTaskAs'), type];
    }

    if (type && action === 'removed') {
      return [t('global.timeline.unflaggedTheTaskAs'), type];
    }

    if (action === 'added') {
      return [t('global.timeline.flaggedTheTask')];
    }

    if (action === 'removed') {
      return [t('global.timeline.unflaggedTheTask')];
    }

    return [];
  });

  return {
    ...baseEventData,
    addedFlags,
    removedFlags,
    flagInfo,
    message
  };
}
